import { useTranslation } from "react-i18next";

import "shared/filterDefinitions";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import { FAILURE_MODE_FILTER } from "shared/filterDefinitions";

import { FilterSchemaItem } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import { useFailureModeSchema } from "./failureModeSchema";
import { SchemaEntryOverride, SchemaRelations, UseSchema } from "./types";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "./utils/schema-utils";
import useVehiclesSchema from "./vehiclesSchema";

export const useRiskModelPredictionsSchema = (
  filterGenerator: (item: FilterSchemaItem) => FilterSchemaItem,
  accessorsToHide: string[] = [],
  vehicleAccessorsToHide: string[] = [],
  failureModeAccessorsToHide: string[] = [],
  showVehiclesSchema: boolean = false,
  showFailureModeSchema: boolean = false
): UseSchema => {
  const { t } = useTranslation();
  const { attributes } = useAttributesContext(
    ContextProviderName.RiskModelPrediction
  );

  const vehiclesSchema = useVehiclesSchema(vehicleAccessorsToHide);
  const failureModeSchema = useFailureModeSchema(failureModeAccessorsToHide);

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(filterGenerator, attributeMap);

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const relations: SchemaRelations = {};
  if (showVehiclesSchema) {
    relations["/api/v1/vehicles"] = vehiclesSchema;
  }

  if (showFailureModeSchema) {
    relations["/api/v1/failureModes"] = failureModeSchema;
  }

  const overrides: SchemaEntryOverride = {
    failureModeID: {
      schemaOverride: {
        label: t("common:event_type.failuremode_one"),
        filter: FAILURE_MODE_FILTER(t),
        sortable: false,
      },
    },
    riskStatus: {
      filterOverride: {
        disableArbitraryText: true,
      },
    },
    riskLabel: {
      filterOverride: {
        disableArbitraryText: true,
      },
    },
  };

  const schema: SchemaEntry[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    relations,
    accessorsToHide,
    true
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  return {
    schema,
    attributes,
    getDisplayLabel,
    attributeMap,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor,
  };
};
